import { Paginator } from 'array-paginator';
import { defineStore } from 'pinia';
import { ResolveListForExportCallback } from 'src/stores/_infrastructure/ResolveListForExportCallback';
import { completeListExportViewFor } from 'src/stores/_infrastructure/completeListExportView';
import { resolveDesktopModuleDateFilterForQuery } from 'src/stores/_infrastructure/dateTimeRangeQueryValue';
import { dedupByAggregateId } from 'src/stores/_infrastructure/dedupByAggregateId';
import {
	COMPLETE_LIST_EXPORT_LIMIT_HEADROOM,
	prepareQueryForCompleteListExport,
} from 'src/stores/_infrastructure/prepareQueryForCompleteListExport';
import { useGenericActivityListStore } from 'src/stores/genericActivityList';
import { GetAllUsersWithAccessToTheModuleForTheUserFilterQuery } from 'src/stores/genericActivityList/queries';
import { DeleteTheSelectedAggregateIdsCommand } from 'src/stores/trashList/commands/DeleteTheSelectedAggregateIdsCommand';
import { MailSpamByAggregateIdsCommand } from 'src/stores/trashList/commands/MailSpamByAggregateIdsCommand';
import { RestoreTheSelectedAggregateIdsCommand } from 'src/stores/trashList/commands/RestoreTheSelectedAggregateIdsCommand';
import {
	TRASH_LIST_GENERIC_ACTIVITY_LIST_SORT,
	TRASH_LIST_GENERIC_ACTIVITY_LIST_VIEW_MODE_LARGE,
} from 'src/stores/trashList/localStorageKeys';
import {
	GetTheTrashModuleBoxesForTheUserQuery,
	GetTheTrashModuleItemsForTheUserQuery,
} from 'src/stores/trashList/queries';
import createTrashListState from 'src/stores/trashList/state';
import { TSMap } from 'typescript-map';

import { DateRangeSelection } from 'src/models/common';
import { FilterBoxes, FilterBoxesRunType, FilterUsers, FilterUsersRunType } from 'src/models/filter';
import {
	ActivityListTypeEnum,
	GenericActivityListPagination,
	GenericActivityListSort,
} from 'src/models/genericActivityList';
import { PreconfiguredTimeRanges } from 'src/models/miniCalendar';
import { ModuleNameEnum } from 'src/models/module/enums';
import { SerializedQuery } from 'src/models/query';
import { StatusEnum } from 'src/models/status/enums';
import { TrashListItem, TrashListItemRunType } from 'src/models/trashList';

import { setTheStateForTheAggregateIds } from 'src/util/aggregate/AggregateUtilityFunctions';
import { createCompareFunction } from 'src/util/collection/customCompareFunction';
import { DEFAULT_LIST_INSTANCE_ID, deriveListStoreId, isDefaultInstance } from 'src/util/listInstance/listInstanceKeys';

import {
	GetAllUsersWithAccessToTheModuleForTheUserFilterTrashListCallBack,
	GetTheAllTrashModuleItemsForTheUserCallBack,
	GetTheMineTrashModuleItemsForTheUserCallBack,
	GetTheOpenTrashModuleItemsForTheUserCallBack,
	GetTheTrashModuleBoxesForTheUserQueryCallBack,
} from './callbacks';
import {
	CompleteListExportFetchPayload,
	DeleteSelectedAggregateIdsPayload,
	ListFetchPayload,
	QueryStorePayload,
	RestoreSelectedAggregateIdsPayload,
	SetDateFilterPayload,
	SetDateTimeRangeFilterPayload,
	SetDirectionFilterPayload,
	SetFilteredBoxesPayload,
	SetFilteredUsersPayload,
	SetListPayload,
	SetListStatePayload,
	SetPaginationPagePayload,
	SetPaginationPayload,
	SetSelectedSharedQueryPayload,
	SetSortPayload,
	SpamSelectedAggregateIdsPayload,
} from './interfaces';

const DEFAULT_ROWS_PER_PAGE = 100;
const DEFAULT_LIST_SORT: GenericActivityListSort = {
	direction: 'DESC',
	property: 'timestamp',
};

const createDefaultPaginationEntry = (): GenericActivityListPagination => ({
	page: 1,
	rowsPerPage: DEFAULT_ROWS_PER_PAGE,
});

const createDefaultPaginationMap = (): TSMap<ActivityListTypeEnum, GenericActivityListPagination> =>
	new TSMap<ActivityListTypeEnum, GenericActivityListPagination>([
		[ActivityListTypeEnum.ALL, createDefaultPaginationEntry()],
		[ActivityListTypeEnum.ALL_MODULE_LISTS_AT_ONCE, createDefaultPaginationEntry()],
		[ActivityListTypeEnum.MINE, createDefaultPaginationEntry()],
		[ActivityListTypeEnum.OPEN, createDefaultPaginationEntry()],
	]);

const replaceActiveQueryStoreSubscription = <T>({
	matchesLogicalView,
	nextSerializedQuery,
	parseQuery,
	queryStore,
	subscriptions,
}: {
	matchesLogicalView: (serializedQuery: SerializedQuery) => boolean;
	nextSerializedQuery: SerializedQuery;
	parseQuery: (serializedQuery: SerializedQuery) => T;
	queryStore: ListFetchPayload['queryStore'];
	subscriptions: SerializedQuery[];
}): SerializedQuery[] => {
	const staleSubscriptions = subscriptions.filter(matchesLogicalView);

	staleSubscriptions.forEach((serializedQuery) => {
		void queryStore?.unsubscribeQuery(parseQuery(serializedQuery));
	});

	return [...subscriptions.filter((serializedQuery) => !matchesLogicalView(serializedQuery)), nextSerializedQuery];
};

// Task 3.1f: isolierte gespeicherte-Listen-Tabs (`$id !== 'trashList'`) sollen view-scoped Zähler/Zeilen zeigen —
// deshalb unterdrücken ihre Query-Konstruktionen die Box→Custom-Kopplung (sonst kollabieren Offen/Meine/Alle auf
// dieselbe Custom-Query → 8/8/8). Der Default-Store behält die klassische Kopplung (byte-identisch). Wird an ALLE
// Konstruktionsstellen (Dispatch + Register + Export) gereicht, damit Subscription-Identifier konsistent bleiben.
const suppressBoxCustomCouplingForInstance = (storeId: string): boolean => storeId !== 'trashList';

const defineTrashListStore = (id: string) =>
	defineStore(id, {
		actions: {
			addQueryStoreSubscription(serializedQuery: SerializedQuery): void {
				this.queryStoreSubscriptions.push(serializedQuery);
			},
			clearQueryStoreSubscriptions(): void {
				this.queryStoreSubscriptions = [];
			},
			clearSubscriptions(payload: QueryStorePayload): void {
				if (payload.queryStore !== undefined) {
					this.queryStoreSubscriptions.forEach((serializedQuery: SerializedQuery) => {
						switch (serializedQuery.type) {
							case 'GetTheTrashModuleItemsForTheUserQuery':
								void payload.queryStore?.unsubscribeQuery(
									GetTheTrashModuleItemsForTheUserQuery.fromJSON<GetTheTrashModuleItemsForTheUserQuery>(
										serializedQuery.query
									)
								);
								break;
							default:
								break;
						}
					});
				}

				this.clearQueryStoreSubscriptions();
			},
			deleteSelectedAggregateIds(payload: DeleteSelectedAggregateIdsPayload): void {
				void payload.commandBus?.sendCommand(
					new DeleteTheSelectedAggregateIdsCommand(payload.aggregateIds, payload.processId)
				);
			},
			async fetchAllTrashList(payload: ListFetchPayload): Promise<void> {
				if (payload.queryStore === undefined) {
					this.setAllTrashList({
						page: 1,
						payload: [],
						totalCount: 0,
					});
					return;
				}

				this.registerUnsubscribeFetchAllTrashList(payload);
				const fetchToken = useGenericActivityListStore().beginListFetch(this.$id, ActivityListTypeEnum.ALL);

				const pagination = this.paginationFor(ActivityListTypeEnum.ALL);
				const page = pagination.page;
				const query = new GetTheTrashModuleItemsForTheUserQuery(
					'all',
					resolveDesktopModuleDateFilterForQuery(this.dateFilter, this.dateTimeRangeFilter),
					this.filteredBoxIds,
					this.filteredUsers.map((filteredUser: FilterUsers) => filteredUser.userId),
					payload.searchTerm,
					payload.hiddenSearchTerm,
					this.directionFilter,
					pagination,
					suppressBoxCustomCouplingForInstance(this.$id)
				);
				const queryIdentifier = query.uniqueQueryIdentifier();

				this.setPaginationPage({ activityListType: ActivityListTypeEnum.ALL, page });
				this.setActiveQueryIdentifier(ActivityListTypeEnum.ALL, queryIdentifier);

				const callback = new GetTheAllTrashModuleItemsForTheUserCallBack(this, page, queryIdentifier);
				callback.bindFetchStateRelease(() => useGenericActivityListStore().endListFetch(fetchToken));
				await payload.queryStore.query<TrashListItem[]>({
					callback,
					createTime: new Date(),
					query,
					responseType: 'json',
					runType: TrashListItemRunType,
				});
			},
			async fetchFilterBoxes(payload: QueryStorePayload): Promise<void> {
				if (payload.queryStore === undefined) {
					this.setFilterBoxes([]);
					return;
				}

				await payload.queryStore.query<Array<FilterBoxes>>({
					callback: new GetTheTrashModuleBoxesForTheUserQueryCallBack(this),
					createTime: new Date(),
					query: new GetTheTrashModuleBoxesForTheUserQuery(),
					responseType: 'json',
					runType: FilterBoxesRunType,
				});
			},
			async fetchFilterUsers(payload: QueryStorePayload): Promise<void> {
				if (payload.queryStore === undefined) {
					this.setFilterUsers([]);
					return;
				}

				await payload.queryStore.query<Array<FilterUsers>>({
					callback: new GetAllUsersWithAccessToTheModuleForTheUserFilterTrashListCallBack(this),
					createTime: new Date(),
					query: new GetAllUsersWithAccessToTheModuleForTheUserFilterQuery(ModuleNameEnum.MODULE_TRASH),
					responseType: 'json',
					runType: FilterUsersRunType,
				});
			},
			async fetchMineTrashList(payload: ListFetchPayload): Promise<void> {
				if (payload.queryStore === undefined) {
					this.setMineTrashList({
						page: 1,
						payload: [],
						totalCount: 0,
					});
					return;
				}

				this.registerUnsubscribeFetchMineTrashList(payload);
				const fetchToken = useGenericActivityListStore().beginListFetch(this.$id, ActivityListTypeEnum.MINE);

				const pagination = this.paginationFor(ActivityListTypeEnum.MINE);
				const page = pagination.page;
				const query = new GetTheTrashModuleItemsForTheUserQuery(
					'mine',
					resolveDesktopModuleDateFilterForQuery(this.dateFilter, this.dateTimeRangeFilter),
					this.filteredBoxIds,
					this.filteredUsers.map((filteredUser: FilterUsers) => filteredUser.userId),
					payload.searchTerm,
					payload.hiddenSearchTerm,
					this.directionFilter,
					pagination,
					suppressBoxCustomCouplingForInstance(this.$id)
				);
				const queryIdentifier = query.uniqueQueryIdentifier();

				this.setPaginationPage({ activityListType: ActivityListTypeEnum.MINE, page });
				this.setActiveQueryIdentifier(ActivityListTypeEnum.MINE, queryIdentifier);

				const callback = new GetTheMineTrashModuleItemsForTheUserCallBack(this, page, queryIdentifier);
				callback.bindFetchStateRelease(() => useGenericActivityListStore().endListFetch(fetchToken));
				await payload.queryStore.query<TrashListItem[]>({
					callback,
					createTime: new Date(),
					query,
					responseType: 'json',
					runType: TrashListItemRunType,
				});
			},
			async fetchOpenTrashList(payload: ListFetchPayload): Promise<void> {
				if (payload.queryStore === undefined) {
					this.setOpenTrashList({
						page: 1,
						payload: [],
						totalCount: 0,
					});
					return;
				}

				this.registerUnsubscribeFetchOpenTrashList(payload);
				const fetchToken = useGenericActivityListStore().beginListFetch(this.$id, ActivityListTypeEnum.OPEN);

				const pagination = this.paginationFor(ActivityListTypeEnum.OPEN);
				const page = pagination.page;
				const query = new GetTheTrashModuleItemsForTheUserQuery(
					'open',
					resolveDesktopModuleDateFilterForQuery(this.dateFilter, this.dateTimeRangeFilter),
					this.filteredBoxIds,
					this.filteredUsers.map((filteredUser: FilterUsers) => filteredUser.userId),
					payload.searchTerm,
					payload.hiddenSearchTerm,
					this.directionFilter,
					pagination,
					suppressBoxCustomCouplingForInstance(this.$id)
				);
				const queryIdentifier = query.uniqueQueryIdentifier();

				this.setPaginationPage({ activityListType: ActivityListTypeEnum.OPEN, page });
				this.setActiveQueryIdentifier(ActivityListTypeEnum.OPEN, queryIdentifier);

				const callback = new GetTheOpenTrashModuleItemsForTheUserCallBack(this, page, queryIdentifier);
				callback.bindFetchStateRelease(() => useGenericActivityListStore().endListFetch(fetchToken));
				await payload.queryStore.query<TrashListItem[]>({
					callback,
					createTime: new Date(),
					query,
					responseType: 'json',
					runType: TrashListItemRunType,
				});
			},
			async fetchTheCompleteListForExport(
				payload: CompleteListExportFetchPayload
			): Promise<TrashListItem[] | null> {
				if (payload.queryStore === undefined) {
					return null;
				}

				const totalCountByListType: Partial<Record<ActivityListTypeEnum, number>> = {
					[ActivityListTypeEnum.ALL]: this.trashListAllTotalCount,
					[ActivityListTypeEnum.MINE]: this.trashListMineTotalCount,
					[ActivityListTypeEnum.OPEN]: this.trashListOpenTotalCount,
				};
				const pagination: GenericActivityListPagination = {
					page: 1,
					rowsPerPage:
						(totalCountByListType[payload.activityListType] ?? 0) + COMPLETE_LIST_EXPORT_LIMIT_HEADROOM,
				};
				const query = prepareQueryForCompleteListExport(
					new GetTheTrashModuleItemsForTheUserQuery(
						completeListExportViewFor(payload.activityListType),
						resolveDesktopModuleDateFilterForQuery(this.dateFilter, this.dateTimeRangeFilter),
						this.filteredBoxIds,
						this.filteredUsers.map((filteredUser: FilterUsers) => filteredUser.userId),
						payload.searchTerm,
						payload.hiddenSearchTerm,
						this.directionFilter,
						pagination,
						suppressBoxCustomCouplingForInstance(this.$id)
					)
				);

				let completeList: TrashListItem[] | null = null;

				await payload.queryStore.query<TrashListItem[]>({
					callback: new ResolveListForExportCallback<TrashListItem>((list) => {
						completeList = list;
					}),
					createTime: new Date(),
					query,
					responseType: 'json',
					runType: TrashListItemRunType,
				});

				return completeList;
			},
			isLatestQueryIdentifier(activityListType: ActivityListTypeEnum, queryIdentifier: string): boolean {
				return (this.activeQueryIdentifierByListType.get(activityListType) ?? '') === queryIdentifier;
			},
			registerUnsubscribeFetchAllTrashList(payload: ListFetchPayload): void {
				if (payload.queryStore === undefined) {
					return;
				}

				const pagination = this.paginationFor(ActivityListTypeEnum.ALL);
				const query = new GetTheTrashModuleItemsForTheUserQuery(
					'all',
					resolveDesktopModuleDateFilterForQuery(this.dateFilter, this.dateTimeRangeFilter),
					this.filteredBoxIds,
					this.filteredUsers.map((filteredUser: FilterUsers) => filteredUser.userId),
					payload.searchTerm,
					payload.hiddenSearchTerm,
					this.directionFilter,
					pagination,
					suppressBoxCustomCouplingForInstance(this.$id)
				);

				this.replaceTrashListQueryStoreSubscription({
					query,
					queryStore: payload.queryStore,
				});
			},
			registerUnsubscribeFetchMineTrashList(payload: ListFetchPayload): void {
				if (payload.queryStore === undefined) {
					return;
				}

				const pagination = this.paginationFor(ActivityListTypeEnum.MINE);
				const query = new GetTheTrashModuleItemsForTheUserQuery(
					'mine',
					resolveDesktopModuleDateFilterForQuery(this.dateFilter, this.dateTimeRangeFilter),
					this.filteredBoxIds,
					this.filteredUsers.map((filteredUser: FilterUsers) => filteredUser.userId),
					payload.searchTerm,
					payload.hiddenSearchTerm,
					this.directionFilter,
					pagination,
					suppressBoxCustomCouplingForInstance(this.$id)
				);

				this.replaceTrashListQueryStoreSubscription({
					query,
					queryStore: payload.queryStore,
				});
			},
			registerUnsubscribeFetchOpenTrashList(payload: ListFetchPayload): void {
				if (payload.queryStore === undefined) {
					return;
				}

				const pagination = this.paginationFor(ActivityListTypeEnum.OPEN);
				const query = new GetTheTrashModuleItemsForTheUserQuery(
					'open',
					resolveDesktopModuleDateFilterForQuery(this.dateFilter, this.dateTimeRangeFilter),
					this.filteredBoxIds,
					this.filteredUsers.map((filteredUser: FilterUsers) => filteredUser.userId),
					payload.searchTerm,
					payload.hiddenSearchTerm,
					this.directionFilter,
					pagination,
					suppressBoxCustomCouplingForInstance(this.$id)
				);

				this.replaceTrashListQueryStoreSubscription({
					query,
					queryStore: payload.queryStore,
				});
			},
			replaceTrashListQueryStoreSubscription(payload: {
				query: GetTheTrashModuleItemsForTheUserQuery;
				queryStore: ListFetchPayload['queryStore'];
			}): void {
				const nextSerializedQuery: SerializedQuery = {
					query: JSON.stringify(payload.query),
					type: 'GetTheTrashModuleItemsForTheUserQuery',
				};
				const expectedStandardFilterSelection = payload.query.standardFilterSelection;

				this.queryStoreSubscriptions = replaceActiveQueryStoreSubscription({
					matchesLogicalView: (serializedQuery) => {
						if (serializedQuery.type !== nextSerializedQuery.type) {
							return false;
						}

						return (
							GetTheTrashModuleItemsForTheUserQuery.fromJSON<GetTheTrashModuleItemsForTheUserQuery>(
								serializedQuery.query
							).standardFilterSelection === expectedStandardFilterSelection
						);
					},
					nextSerializedQuery,
					parseQuery: (serializedQuery) =>
						GetTheTrashModuleItemsForTheUserQuery.fromJSON<GetTheTrashModuleItemsForTheUserQuery>(
							serializedQuery.query
						),
					queryStore: payload.queryStore,
					subscriptions: this.queryStoreSubscriptions,
				});
			},
			reset(payload: QueryStorePayload): void {
				this.clearSubscriptions({
					queryStore: payload.queryStore,
				});
				this.resetState();
			},
			resetState(): void {
				const today = new Date().toISOString().split('T')[0];
				this.activeQueryIdentifierByListType = new TSMap<ActivityListTypeEnum, string>([
					[ActivityListTypeEnum.ALL, ''],
					[ActivityListTypeEnum.MINE, ''],
					[ActivityListTypeEnum.OPEN, ''],
				]);
				this.dateFilter = today;
				this.today = today;
				this.dateTimeRangeFilter = PreconfiguredTimeRanges.TODAY;
				this.filteredUsers = [];
				this.filteredBoxIds = [];
				this.directionFilter = 'combined';
				this.selectedSharedQueryId = '';
				this.queryStoreSubscriptions = [];
				this.trashListOpenTotalCount = 0;
				this.trashListOpen = [];
				this.trashListOpenSliced.clear();
				this.trashListAllTotalCount = 0;
				this.trashListAll = [];
				this.trashListAllSliced.clear();
				this.trashListMineTotalCount = 0;
				this.trashListMine = [];
				this.trashListMineSliced.clear();
				this.pagination = createDefaultPaginationMap();
			},
			restoreSelectedAggregateIds(payload: RestoreSelectedAggregateIdsPayload): void {
				void payload.commandBus?.sendCommand(new RestoreTheSelectedAggregateIdsCommand(payload.aggregateIds));
			},
			setActiveQueryIdentifier(activityListType: ActivityListTypeEnum, queryIdentifier: string): void {
				this.activeQueryIdentifierByListType.set(activityListType, queryIdentifier);
			},
			setAllTrashList(payload: SetListPayload): void {
				this.trashListAllTotalCount = payload.totalCount;
				this.trashListAllSliced.set(payload.page, payload.payload);

				const sort = this.sortFor(ActivityListTypeEnum.ALL);
				this.trashListAll = dedupByAggregateId(
					[...this.trashListAllSliced.values()].flatMap((entry) => entry)
				).sort(createCompareFunction<TrashListItem>(sort.property as keyof TrashListItem, sort.direction));
			},
			setDateFilterAction(payload: SetDateFilterPayload): void {
				this.setDateFilterValue(payload.dateFilter);
			},
			setDateFilterValue(payload: DateRangeSelection | string): void {
				this.dateFilter = payload || '';
			},
			setDateTimeRangeFilter(payload: SetDateTimeRangeFilterPayload): void {
				this.setDateTimeRangeFilterValue(payload.dateTimeRangeFilter);
			},
			setDateTimeRangeFilterValue(payload: PreconfiguredTimeRanges): void {
				this.dateTimeRangeFilter = payload;
			},
			setDirectionFilterAction(payload: SetDirectionFilterPayload): void {
				this.setDirectionFilterValue(payload.directionFilter);
			},
			setDirectionFilterValue(payload: 'combined' | 'in' | 'out'): void {
				this.directionFilter = payload;
			},
			setFilterBoxes(payload: Array<FilterBoxes>): void {
				this.filterBoxes = payload || [];
			},
			setFilterUsers(payload: Array<FilterUsers>): void {
				this.filterUsers = payload || [];
			},
			setFilteredBoxes(payload: Array<string>): void {
				this.filteredBoxIds = payload || [];
			},
			setFilteredBoxesAction(payload: SetFilteredBoxesPayload): void {
				this.setFilteredBoxes(payload.boxIdFilter);
			},
			setFilteredUsers(payload: Array<FilterUsers>): void {
				this.filteredUsers = payload || [];
			},
			setFilteredUsersAction(payload: SetFilteredUsersPayload): void {
				this.setFilteredUsers(payload.filterUsers);
			},
			setListLoadError(): void {
				const genericActivityListStore = useGenericActivityListStore();
				genericActivityListStore.setListLoadError();
			},
			setListViewModeLarge(payload: boolean): void {
				if (this.$id === 'trashList') {
					localStorage.setItem(TRASH_LIST_GENERIC_ACTIVITY_LIST_VIEW_MODE_LARGE, JSON.stringify(payload));
				}
				this.listViewModeLarge = payload;
			},
			setMineTrashList(payload: SetListPayload): void {
				this.trashListMineTotalCount = payload.totalCount;
				this.trashListMineSliced.set(payload.page, payload.payload);

				const sort = this.sortFor(ActivityListTypeEnum.MINE);
				this.trashListMine = dedupByAggregateId(
					[...this.trashListMineSliced.values()].flatMap((entry) => entry)
				).sort(createCompareFunction<TrashListItem>(sort.property as keyof TrashListItem, sort.direction));
			},
			setOpenTrashList(payload: SetListPayload): void {
				this.trashListOpenTotalCount = payload.totalCount;
				this.trashListOpenSliced.set(payload.page, payload.payload);

				const sort = this.sortFor(ActivityListTypeEnum.OPEN);
				this.trashListOpen = dedupByAggregateId(
					[...this.trashListOpenSliced.values()].flatMap((entry) => entry)
				).sort(createCompareFunction<TrashListItem>(sort.property as keyof TrashListItem, sort.direction));
			},
			setPagination(payload: SetPaginationPayload): void {
				this.pagination.set(payload.activityListType, payload.pagination);
			},
			setPaginationAction(payload: SetPaginationPayload): void {
				this.setPagination(payload);
			},
			setPaginationPage(payload: SetPaginationPagePayload): void {
				const pagination = this.paginationFor(payload.activityListType);
				pagination.page = payload.page;
				this.pagination.set(payload.activityListType, pagination);
			},
			setPaginationPageAction(payload: SetPaginationPagePayload): void {
				this.setPaginationPage(payload);
			},
			setSelectedSharedQuery(payload: string): void {
				this.selectedSharedQueryId = payload || '';
			},
			setSelectedSharedQueryAction(payload: SetSelectedSharedQueryPayload): void {
				this.setSelectedSharedQuery(payload.selectedSharedQuery ?? '');
			},
			setSort(payload: SetSortPayload): void {
				this.sort.set(payload.activityListType, payload.sort);
			},
			setSortAction(payload: SetSortPayload): void {
				this.setSort(payload);
				if (this.$id === 'trashList') {
					localStorage.setItem(TRASH_LIST_GENERIC_ACTIVITY_LIST_SORT, JSON.stringify(this.sort.toJSON()));
				}
			},
			setTheStateForAggregateIds(aggregateIds: string[], status: StatusEnum): void {
				let openCountDelta = 0;
				aggregateIds.forEach((aggregateId) => {
					const known =
						this.trashListOpen.find((item) => item.aggregateId === aggregateId) ??
						this.trashListAll.find((item) => item.aggregateId === aggregateId) ??
						this.trashListMine.find((item) => item.aggregateId === aggregateId);
					if (!known) {
						return;
					}
					const wasReady = known.statusButton.status === StatusEnum.READY;
					const willBeReady = status === StatusEnum.READY;
					if (!wasReady && willBeReady) {
						openCountDelta -= 1;
					} else if (wasReady && !willBeReady) {
						openCountDelta += 1;
					}
				});
				if (openCountDelta !== 0) {
					this.trashListOpenTotalCount = Math.max(0, this.trashListOpenTotalCount + openCountDelta);
				}

				const setPagedList = <T>(
					entries: T[],
					totalCount: number,
					applyPage: (payload: { page: number; payload: T[]; totalCount: number }) => void
				): void => {
					if (entries.length === 0) {
						return;
					}

					const paginator = new Paginator(entries, DEFAULT_ROWS_PER_PAGE, 1);
					while (paginator.current <= paginator.total) {
						applyPage({
							page: paginator.current,
							payload: paginator.page(paginator.current) as T[],
							totalCount: totalCount,
						});
						paginator.next();
					}
				};

				setPagedList<TrashListItem>(
					setTheStateForTheAggregateIds(
						this.trashListOpen as never[],
						aggregateIds,
						status
					) as unknown as TrashListItem[],
					this.trashListOpenTotalCount,
					(payload) => this.setOpenTrashList(payload)
				);
				setPagedList<TrashListItem>(
					setTheStateForTheAggregateIds(
						this.trashListAll as never[],
						aggregateIds,
						status
					) as unknown as TrashListItem[],
					this.trashListAllTotalCount,
					(payload) => this.setAllTrashList(payload)
				);
				setPagedList<TrashListItem>(
					setTheStateForTheAggregateIds(
						this.trashListMine as never[],
						aggregateIds,
						status
					) as unknown as TrashListItem[],
					this.trashListMineTotalCount,
					(payload) => this.setMineTrashList(payload)
				);
			},
			setTheTrashStateToReady(payload: SetListStatePayload): void {
				this.setTheStateForAggregateIds(payload.aggregateIds, StatusEnum.READY);
			},
			setTheTrashStateToSeen(payload: SetListStatePayload): void {
				this.setTheStateForAggregateIds(payload.aggregateIds, StatusEnum.SEEN);
			},
			setTheTrashStateToUnseen(payload: SetListStatePayload): void {
				this.setTheStateForAggregateIds(payload.aggregateIds, StatusEnum.UNSEEN);
			},
			setToday(payload: string): void {
				this.today = payload || '';
			},
			silentSetAllTrashList(): void {
				this.trashListAllTotalCount = 0;
				this.trashListAll = [];
				this.trashListAllSliced.clear();
				this.pagination.set(ActivityListTypeEnum.ALL, createDefaultPaginationEntry());
			},
			silentSetMineTrashList(): void {
				this.trashListMineTotalCount = 0;
				this.trashListMine = [];
				this.trashListMineSliced.clear();
				this.pagination.set(ActivityListTypeEnum.MINE, createDefaultPaginationEntry());
			},
			silentSetOpenTrashList(): void {
				this.trashListOpenTotalCount = 0;
				this.trashListOpen = [];
				this.trashListOpenSliced.clear();
				this.pagination.set(ActivityListTypeEnum.OPEN, createDefaultPaginationEntry());
			},
			spamSelectedAggregateIds(payload: SpamSelectedAggregateIdsPayload): void {
				void payload.commandBus?.sendCommand(
					new MailSpamByAggregateIdsCommand(payload.aggregateIds, payload.processId)
				);
			},
		},
		getters: {
			paginationFor(state): (activityListType: ActivityListTypeEnum) => GenericActivityListPagination {
				return (activityListType: ActivityListTypeEnum): GenericActivityListPagination =>
					state.pagination.get(activityListType) ?? createDefaultPaginationEntry();
			},
			sortFor(state): (activityListType: ActivityListTypeEnum) => GenericActivityListSort {
				return (activityListType: ActivityListTypeEnum): GenericActivityListSort =>
					state.sort.get(activityListType) ?? { ...DEFAULT_LIST_SORT };
			},
		},
		state: createTrashListState,
	});

export const useTrashListStore = defineTrashListStore('trashList');

const trashListInstanceDefinitions = new Map<string, ReturnType<typeof defineTrashListStore>>();

export const createTrashListStore = (instanceId: string = DEFAULT_LIST_INSTANCE_ID) => {
	if (isDefaultInstance(instanceId)) {
		return useTrashListStore();
	}

	const storeId = deriveListStoreId('trashList', instanceId);
	let definition = trashListInstanceDefinitions.get(storeId);

	if (definition === undefined) {
		definition = defineTrashListStore(storeId);
		trashListInstanceDefinitions.set(storeId, definition);
	}

	return definition();
};

export * from './interfaces';
export * from './types';
